import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUpRight, ShieldAlert, Wallet, Activity, AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function MerchantOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/merchant/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/merchants/me").then((r) => setMerchant(r.data && r.data.merchant_id ? r.data : null)).catch(() => {}),
      api.get("/transactions").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {}),
    ]);
  }, []);

  return (
    <div data-testid="merchant-overview">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#00D533]">Dashboard</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
        </div>
        <Link to="/dashboard/transactions/new">
          <Button className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold" data-testid="overview-new-charge-btn">
            <Plus className="w-4 h-4 mr-2" /> New Charge
          </Button>
        </Link>
      </div>

      {!merchant && (
        <div className="sk-card p-6 mb-8 border-[#F59E0B]/30 bg-[#FEF9EC]" data-testid="overview-apply-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#F59E0B] mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium">Complete your merchant application</h3>
              <p className="text-sm text-slate-500 mt-1">
                Submit your business profile to unlock processing limits and underwriting decisions.
              </p>
            </div>
            <Link to="/dashboard/apply">
              <Button variant="ghost" className="border border-slate-300 hover:bg-slate-50">
                Start application <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {merchant && (
        <div className="sk-card p-6 mb-8" data-testid="overview-merchant-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">Merchant Profile</div>
              <h3 className="mt-1 font-heading text-xl">{merchant.legal_business_name}</h3>
              <p className="text-sm text-slate-500 mt-1">{merchant.industry} • {merchant.country}</p>
            </div>
            <div className="flex items-center gap-3">
              <StatusPill status={merchant.status} />
              {merchant.risk_tier && <RiskPill tier={merchant.risk_tier} />}
              {merchant.approved_limit && (
                <div className="text-right">
                  <div className="text-xs text-slate-400 uppercase">Approved Limit</div>
                  <div className="font-mono text-lg">${Number(merchant.approved_limit).toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={Wallet} label="Total Volume" value={`$${(stats?.total_volume || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} accent="#00D533" />
        <StatCard icon={Activity} label="Transactions" value={stats?.total_transactions ?? 0} sub={`${stats?.captured ?? 0} captured / ${stats?.pending ?? 0} pending`} />
        <StatCard icon={ShieldAlert} label="High-Risk" value={stats?.high_risk_transactions ?? 0} accent="#FF4757" />
        <StatCard icon={AlertTriangle} label="Open Chargebacks" value={stats?.open_chargebacks ?? 0} sub={`${stats?.total_chargebacks ?? 0} total`} accent="#F59E0B" />
      </div>

      <div className="sk-card p-6" data-testid="overview-recent-tx">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-lg">Recent Transactions</h3>
          <Link to="/dashboard/transactions" className="text-sm text-[#00D533] hover:underline">
            View all →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">No transactions yet — try a new charge.</div>
        ) : (
          <div className="space-y-2">
            {recent.map((tx) => (
              <div key={tx.transaction_id} className="grid grid-cols-12 items-center px-3 py-3 rounded-md hover:bg-slate-50 text-sm">
                <div className="col-span-4 truncate">
                  <div className="font-mono text-xs text-slate-400">{tx.transaction_id}</div>
                  <div className="text-slate-700">{tx.description || tx.type.toUpperCase()}</div>
                </div>
                <div className="col-span-2 text-slate-500 capitalize">{tx.type}</div>
                <div className="col-span-2"><RiskPill tier={tx.risk_tier} /></div>
                <div className="col-span-2"><StatusPill status={tx.status} /></div>
                <div className="col-span-2 text-right font-mono">${Number(tx.amount).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent = "#FFFFFF" }) {
  return (
    <div className="sk-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="mt-3 font-heading text-2xl font-semibold" style={{ color: accent === "#FFFFFF" ? "#FFFFFF" : accent }}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    captured: { c: "#10B981", l: "Captured" },
    pending: { c: "#F59E0B", l: "Pending" },
    pending_review: { c: "#F59E0B", l: "Review" },
    expired: { c: "#71717A", l: "Expired" },
    approved: { c: "#10B981", l: "Approved" },
    rejected: { c: "#FF4757", l: "Rejected" },
    open: { c: "#3B82F6", l: "Open" },
    evidence_submitted: { c: "#F59E0B", l: "Evidence" },
    won: { c: "#10B981", l: "Won" },
    lost: { c: "#FF4757", l: "Lost" },
  };
  const it = map[status] || { c: "#71717A", l: status || "—" };
  return (
    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider"
      style={{ background: `${it.c}1A`, color: it.c, border: `1px solid ${it.c}33` }}>
      {it.l}
    </span>
  );
}

export function RiskPill({ tier }) {
  if (!tier) return <span className="text-xs text-slate-400">—</span>;
  const map = {
    low: { c: "#10B981" },
    medium: { c: "#F59E0B" },
    high: { c: "#FF4757" },
  };
  const it = map[tier] || { c: "#71717A" };
  const glow = tier === "high" ? "drop-shadow-[0_0_8px_rgba(255,71,87,0.5)]" : "";
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${glow}`}
      style={{ background: `${it.c}1A`, color: it.c, border: `1px solid ${it.c}33` }}>
      {tier}
    </span>
  );
}
